import * as chatApi from "@lib/chatApi";
import type { ChatPlan, ChatPlanStepStatus } from "@lib/types";
import { Check, Loader2, X } from "lucide-react";
import { useState } from "react";

interface PlanCardProps {
  plan: ChatPlan;
}

const STATUS_LABEL: Record<ChatPlan["state"], string> = {
  proposed: "Awaiting your approval",
  approved_run_all: "Approved — running",
  approved_step_by_step: "Approved — step by step",
  executing: "Running",
  step_paused: "Paused — review the next step",
  completed: "Completed",
  rejected: "Rejected",
  cancelled: "Cancelled",
  superseded: "Superseded by a newer plan",
};

function StepIcon({ status }: { status: ChatPlanStepStatus }) {
  if (status === "running") {
    return <Loader2 size={16} className="animate-spin text-accent" />;
  }
  if (status === "completed") {
    return (
      <span className="flex h-4 w-4 items-center justify-center rounded-sm bg-emerald-600 text-white">
        <Check size={12} strokeWidth={3} />
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span className="flex h-4 w-4 items-center justify-center rounded-sm bg-red-600 text-white">
        <X size={12} strokeWidth={3} />
      </span>
    );
  }
  if (status === "skipped") {
    return (
      <span className="flex h-4 w-4 items-center justify-center rounded-sm border border-text-tertiary/40 bg-surface-secondary text-text-tertiary">
        <X size={10} strokeWidth={2.5} />
      </span>
    );
  }
  return <span className="block h-4 w-4 rounded-sm border border-text-tertiary/50" />;
}

export function PlanCard({ plan }: PlanCardProps) {
  const [busy, setBusy] = useState(false);
  const [showRequestChanges, setShowRequestChanges] = useState(false);
  const [feedback, setFeedback] = useState("");

  const isProposed = plan.state === "proposed";
  const isPaused = plan.state === "step_paused";
  const isInFlight =
    plan.state === "executing" ||
    plan.state === "approved_run_all" ||
    plan.state === "approved_step_by_step";

  async function handleApprove(mode: "run_all" | "step_by_step") {
    setBusy(true);
    try {
      await chatApi.approvePlan(plan.id, mode);
    } finally {
      setBusy(false);
    }
  }

  async function handleRequestChanges() {
    if (!feedback.trim()) return;
    setBusy(true);
    try {
      await chatApi.requestPlanChanges(plan.id, feedback.trim());
      setShowRequestChanges(false);
      setFeedback("");
    } finally {
      setBusy(false);
    }
  }

  async function handleReject() {
    setBusy(true);
    try {
      await chatApi.rejectPlan(plan.id);
    } finally {
      setBusy(false);
    }
  }

  async function handleContinue() {
    setBusy(true);
    try {
      await chatApi.continuePlan(plan.id);
    } finally {
      setBusy(false);
    }
  }

  async function handleCancel() {
    setBusy(true);
    try {
      await chatApi.cancelPlan(plan.id);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className={`rounded-lg border bg-surface-primary p-3 text-sm shadow-sm ${
        isProposed ? "border-accent ring-1 ring-accent/30" : "border-accent/40"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-accent">
            {isProposed ? "Proposed plan — your approval needed" : "Plan"}
          </div>
          {isProposed && (
            <div className="mt-0.5 text-xs text-text-tertiary">
              I'd like to do the following before making any changes. Review the steps and approve,
              run them one at a time, or reject with feedback.
            </div>
          )}
          <div className="mt-1 font-medium text-text-primary">{plan.summary}</div>
        </div>
        {!isProposed && (
          <span className="rounded-full bg-surface-secondary px-2 py-0.5 text-xs text-text-secondary">
            {STATUS_LABEL[plan.state] || plan.state}
          </span>
        )}
      </div>

      <ol className="mt-3 space-y-1.5">
        {plan.steps.map((step, idx) => {
          const isCurrent = idx === plan.current_step_index && isInFlight;
          return (
            <li
              // biome-ignore lint/suspicious/noArrayIndexKey: plan steps are append-only and never reordered
              key={`${plan.id}-${idx}`}
              className={`flex gap-2 ${isCurrent ? "font-medium text-text-primary" : "text-text-secondary"}`}
            >
              <span className="mt-0.5">
                <StepIcon status={step.status} />
              </span>
              <span>{step.description}</span>
            </li>
          );
        })}
      </ol>

      {isProposed && !showRequestChanges && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => handleApprove("run_all")}
            className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-60"
          >
            Approve & run all
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => handleApprove("step_by_step")}
            className="rounded-md border border-accent/40 px-3 py-1.5 text-xs font-medium text-accent hover:bg-accent/10 disabled:opacity-60"
          >
            Run step by step
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => setShowRequestChanges(true)}
            className="rounded-md border border-text-tertiary/30 px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-secondary disabled:opacity-60"
          >
            Request changes…
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={handleReject}
            className="rounded-md border border-text-tertiary/30 px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-secondary disabled:opacity-60"
          >
            Reject
          </button>
        </div>
      )}

      {isProposed && showRequestChanges && (
        <div className="mt-3 space-y-2">
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="What would you like to change? (e.g. use the CFO campaign instead)"
            className="w-full rounded-md border border-text-tertiary/30 bg-surface-primary p-2 text-xs text-text-primary"
            rows={3}
          />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy || !feedback.trim()}
              onClick={handleRequestChanges}
              className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-60"
            >
              Send feedback
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setShowRequestChanges(false);
                setFeedback("");
              }}
              className="rounded-md border border-text-tertiary/30 px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-secondary disabled:opacity-60"
            >
              Back
            </button>
          </div>
        </div>
      )}

      {isPaused && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={handleContinue}
            className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-60"
          >
            Run next step
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={handleCancel}
            className="rounded-md border border-text-tertiary/30 px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-secondary disabled:opacity-60"
          >
            Cancel plan
          </button>
        </div>
      )}

      {plan.state === "executing" && (
        <div className="mt-3">
          <button
            type="button"
            disabled={busy}
            onClick={handleCancel}
            className="rounded-md border border-text-tertiary/30 px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-secondary disabled:opacity-60"
          >
            Cancel plan
          </button>
        </div>
      )}

      {plan.state === "rejected" && plan.rejection_feedback && (
        <p className="mt-3 text-xs italic text-text-tertiary">
          Feedback: {plan.rejection_feedback}
        </p>
      )}
    </div>
  );
}
